import { useState } from 'react';
import {
  cleanInterest, MAX_INTERESTS, MAX_INTEREST_LENGTH, normalizeInterests,
} from '../lib/interests.js';

export default function InterestEditor({
  id, value, onChange, autoFocus = false, suggested = [],
}) {
  const [draft, setDraft] = useState('');
  const interests = normalizeInterests(value);
  const room = MAX_INTERESTS - interests.length;
  // A tag already chosen is not a suggestion, and a suggestion cannot be
  // offered into a list with no room left in it.
  const offers = suggested
    .map(cleanInterest)
    .filter((tag) => tag && !interests.includes(tag))
    .slice(0, 8);

  function add(raw = draft) {
    const additions = String(raw).split(',').map(cleanInterest).filter(Boolean);
    if (additions.length) onChange(normalizeInterests([...interests, ...additions]));
    setDraft('');
  }

  function remove(interest) {
    onChange(interests.filter((item) => item !== interest));
  }

  return (
    <div className="interest-editor">
      {interests.length > 0 && (
        <div className="interest-list" aria-label="Selected interests">
          {interests.map((interest) => (
            <button
              key={interest}
              type="button"
              className="interest-chip editable"
              onClick={() => remove(interest)}
              aria-label={`Remove ${interest}`}
              title="Remove"
            >
              {interest}<span aria-hidden="true">×</span>
            </button>
          ))}
        </div>
      )}

      {interests.length < MAX_INTERESTS && (
        <input
          id={id}
          value={draft}
          autoFocus={autoFocus}
          maxLength={MAX_INTEREST_LENGTH * 2}
          placeholder={interests.length ? 'Add another interest…' : 'e.g. urban design, jazz, robotics'}
          onChange={(event) => {
            const next = event.target.value;
            if (next.includes(',')) add(next);
            else setDraft(next);
          }}
          onBlur={() => { if (draft.trim()) add(); }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ',') {
              event.preventDefault();
              add();
            } else if (event.key === 'Backspace' && !draft && interests.length) {
              remove(interests[interests.length - 1]);
            }
          }}
        />
      )}

      {/* Read off the links on this profile and offered, never applied.
          Accepting one writes it exactly where a typed interest goes — a
          scraped keyword is not something this person did, and the derived
          half of their interests has only ever meant that. */}
      {offers.length > 0 && room > 0 && (
        <div className="suggested-interests">
          <span className="suggested-label">From your links</span>
          <div className="interest-list" aria-label="Suggested interests">
            {offers.map((tag) => (
              <button
                key={tag}
                type="button"
                className="interest-chip suggested"
                onClick={() => add(tag)}
                title={`Add ${tag} to your interests`}
                aria-label={`Add ${tag} to your interests`}
              >
                {tag}<span aria-hidden="true">+</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <p className="hint interest-hint">
        Press Enter after each one · {interests.length}/{MAX_INTERESTS}
      </p>
    </div>
  );
}
